'use client';
import { useMemo } from 'react';
import type { ChoiceFieldProps } from 'elowen-plugin-ui-kit';
import { RowPicker, type RowPickerItem } from './RowPicker';

/** Canonical single-choice FORM field: always the shared anchored dropdown ({@link RowPicker}), whatever
 *  the number of choices, so a two-way theme and a long catalog look and behave alike. Segmented stays for
 *  section tabs and filters, never for a field. Unknown persisted values remain selectable so opening the
 *  UI never drops data.
 *
 *  The dropdown is a ROW CONTROL: one line tall and the width of the record's trailing cell. An option's
 *  `description` and `action` are the dropdown row's second line and trailing control
 *  ({@link RowPickerItem}). */
export function ChoiceField({ title, options, value, onChange, manageAriaLabel }: ChoiceFieldProps) {
  const items = useMemo<RowPickerItem[]>(() => {
    const known = new Set(options.map((option) => option.value));
    return [
      ...(value && !known.has(value) ? [{ id: value, label: value, group: '' }] : []),
      ...options.map((option) => ({ id: option.value, label: option.label, group: '', icon: option.icon, description: option.description, action: option.action })),
    ];
  }, [options, value]);
  const selected = items.find((item) => item.id === value);
  return (
    <RowPicker
      label={manageAriaLabel ?? title}
      summary={selected?.label ?? value}
      icon={selected?.icon}
      items={items}
      value={value}
      onChange={onChange}
    />
  );
}
